import {
  type DynamicClient,
  getActiveNetworkData,
} from '@dynamic-labs-sdk/client';
import { assertDefined } from '@dynamic-labs-sdk/client/core';
import { erc20Abi } from 'viem';

import type { EvmWalletAccount } from '../../EvmWalletAccount.types';
import { createPublicClientFromNetworkData } from '../../viem/createPublicClientFromNetworkData';

type GetErc20AllowanceParams = {
  ownerAddress: string;
  spenderAddress: string;
  tokenAddress: string;
  walletAccount: EvmWalletAccount;
};

/**
 * Reads the current ERC-20 token allowance granted by an owner to a spender.
 *
 * Uses a viem public client derived from the wallet account's active network
 * to call the `allowance(owner, spender)` method on the token contract.
 *
 * @param params.tokenAddress - The ERC-20 token contract address.
 * @param params.ownerAddress - The address that granted the allowance.
 * @param params.spenderAddress - The address allowed to spend the tokens.
 * @param params.walletAccount - The EVM wallet account used to resolve the active network.
 * @param client - The Dynamic client instance.
 * @returns The current allowance as a bigint in the token's smallest unit.
 */
export const getErc20Allowance = async (
  {
    ownerAddress,
    spenderAddress,
    tokenAddress,
    walletAccount,
  }: GetErc20AllowanceParams,
  client: DynamicClient
): Promise<bigint> => {
  const { networkData } = await getActiveNetworkData({ walletAccount }, client);

  assertDefined(
    networkData,
    `No network data found for wallet account ${walletAccount.address}`
  );

  const publicClient = createPublicClientFromNetworkData({ networkData });

  return publicClient.readContract({
    abi: erc20Abi,
    address: tokenAddress as `0x${string}`,
    args: [ownerAddress as `0x${string}`, spenderAddress as `0x${string}`],
    functionName: 'allowance',
  });
};
